import { Ref, useEffect, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import { SetupGuideDocs } from '../../docs/SetupGuideDocs/SetupGuideDocs';
import { Integration, RawIntegration } from '../../shared/interfaces/integration';
import { PipelineSetup } from './components/PipelineSetup/PipelineSetup';
import { PipelineLoadModeField } from './components/PipelineLoadModeField/PipelineLoadModeField';
import { PipelineFailureHandlingPolicyField } from './components/PipelineFailureHandlingPolicyField/PipelineFailureHandlingPolicyField';
import { PipelineSchemaEvolutionField } from './components/PipelineSchemaEvolutionField/PipelineSchemaEvolutionField';
import { PipelineSettingsFormState } from './interfaces/pipeline-settings-form-state';
import styles from './PipelineSettings.module.scss';
import { PipelineAPI } from '../api/PipelineAPI';
import { Banner } from '../../banner/Banner';
import { PipelineSettingsSyncFrequency } from '../PipelineSyncFrequency/PipelineSettingsSyncFrequency';
import { AsyncValidationForm } from '../../shared/components/AsyncValidationForm/AsyncValidationForm';
import { PIPELINE_SETTINGS_DATA_IDS_CONTRACT } from './pipelineSettingsDataIdContract';
import { getErrorMessageFromAxiosError } from '../../request/utils';
import { ResolvedComponent } from '../../routing/ResolvedComponent';
import {
  PipelineSettingsFeatures,
  PipelineSettingsFeaturesData
} from '../interfaces/pipeline-settings-config';
import { DestinationPartitionKey } from './components/DestinationPartitionKey/DestinationPartitionKey';
import { MutationState } from '../../request/interfaces/entity-ui-state';
import { usePipelineSettingsForm } from './hooks/usePipelineSettingsForm';
import { DestinationTableSettings } from './components/DestinationTableSettings/DestinationTableSettings';
import { PipelineReplicationType } from './components/PipelineReplicationType/PipelineReplicationType';
import { PipelineLatency } from './components/PipelineLatency/PipelineLatency';
import { UpgradePipelineBanner } from '../UpgradePipeline/UpgradePipelineBanner';
import { useCreatePipelineContext } from '../CreatePipeline/hooks/useCreatePipelineContext';

export interface PipelineSettingsInnerState {
  isValidating: boolean;
  savingPipeline: boolean;
}

export interface PipelineSettingsImperativeRef {
  submitForm: VoidFunction;
}

export interface PipelineSettingsProps {
  pipeline: Integration;
  onSavePipeline: (rawPipeline: RawIntegration) => void;
  formImperativeRef?: Ref<PipelineSettingsImperativeRef>;
  onStateUpdate?: (state: PipelineSettingsInnerState) => void;
  isEditing: boolean;
}

export interface PipelineSettingsInnerProps extends PipelineSettingsProps {
  features: PipelineSettingsFeaturesData;
  defaultPipelineName: string;
}

export function PipelineSettings(props: PipelineSettingsProps) {
  const { pipeline, onSavePipeline, formImperativeRef, onStateUpdate, isEditing } = props;

  const featuresResolver = () => {
    return PipelineAPI.getIntegrationFeatures(pipeline.id).then(res => res.data);
  };

  const defaultPipelineResolver = () => {
    if (isEditing) {
      return Promise.resolve(pipeline.name);
    }
    return PipelineAPI.getDefaultPipelineName().then(res => res.data.entity_name);
  };

  return (
    <ResolvedComponent
      Component={PipelineSettingsWithAsyncValidationForm}
      componentProps={{
        onSavePipeline,
        formImperativeRef,
        onStateUpdate,
        pipeline,
        isEditing
      }}
      resolve={{
        features: featuresResolver,
        defaultPipelineName: defaultPipelineResolver
      }}
    />
  );
}

export function PipelineSettingsWithAsyncValidationForm(props: PipelineSettingsInnerProps) {
  return (
    <AsyncValidationForm>
      <PipelineSettingsInner {...props} />
    </AsyncValidationForm>
  );
}

function PipelineSettingsInner({
  pipeline,
  onSavePipeline,
  features,
  formImperativeRef,
  defaultPipelineName,
  onStateUpdate,
  isEditing
}: PipelineSettingsInnerProps) {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const formikRef = useRef<FormikProps<PipelineSettingsFormState>>(null);
  const { standardPipelineInfo, isUpgradingPipeline } = useCreatePipelineContext();

  const {
    initialFormValues,
    validationSchema,
    mutationState,
    isValidating,
    error,
    isFormInteractionBlocked,
    disableNonEditableFields,
    disableEditableFields,
    submitForm
  } = usePipelineSettingsForm({
    pipeline,
    onSavePipeline,
    formContainerRef,
    formikRef,
    features,
    defaultPipelineName,
    isEditing
  });

  useImperativeHandle(formImperativeRef, () => ({
    submitForm
  }));

  useEffect(() => {
    if (onStateUpdate) {
      onStateUpdate({
        isValidating,
        savingPipeline: mutationState === MutationState.MUTATING
      });
    }
  }, [isValidating, mutationState]);

  return (
    <div className='d-flex h-100'>
      <Formik
        initialValues={initialFormValues.current}
        validationSchema={validationSchema}
        onSubmit={() => {}}
        innerRef={formikRef}
      >
        {(formikProps: FormikProps<PipelineSettingsFormState>) => {
          return (
            <form
              noValidate
              className='flex-col align-items-center w-100'
              onSubmit={formikProps.handleSubmit}
              data-id={PIPELINE_SETTINGS_DATA_IDS_CONTRACT.form}
            >
              <div
                className='flex-col align-items-center flex-1 scrollable-content w-100'
                data-id={PIPELINE_SETTINGS_DATA_IDS_CONTRACT.base}
                ref={formContainerRef}
              >
                <div
                  className={styles.pipelineSettingsFormContent}
                  data-id={PIPELINE_SETTINGS_DATA_IDS_CONTRACT.formContent}
                >
                  <div
                    className={clsx(
                      styles.formContainer,
                      isUpgradingPipeline && styles.upgradingPipelineFormContentContainer
                    )}
                  >
                    {isUpgradingPipeline ? (
                      <UpgradePipelineBanner
                        standardPipelineInfo={standardPipelineInfo}
                        isObjectConfigPage={false}
                      />
                    ) : null}

                    <PipelineSetup isEditing={isEditing} disabled={disableEditableFields} />

                    {PipelineSettingsFeatures.PARTITION_KEY in features.features ? (
                      <DestinationPartitionKey
                        className='mt-5'
                        disabled={disableNonEditableFields}
                      />
                    ) : null}

                    {PipelineSettingsFeatures.LOAD_MODE in features.features ? (
                      <PipelineLoadModeField
                        disabled={disableEditableFields}
                        featureOptions={features.features[PipelineSettingsFeatures.LOAD_MODE]!}
                      />
                    ) : null}

                    <PipelineFailureHandlingPolicyField disabled={disableEditableFields} />

                    <PipelineSchemaEvolutionField disabled={disableEditableFields} />

                    {PipelineSettingsFeatures.DESTINATION_TABLE_SETTINGS in features.features ? (
                      <DestinationTableSettings
                        disabled={disableNonEditableFields}
                        featureOptions={
                          features.features[PipelineSettingsFeatures.DESTINATION_TABLE_SETTINGS]!
                        }
                      />
                    ) : null}

                    {PipelineSettingsFeatures.REPLICATION_TYPE in features.features ? (
                      <PipelineReplicationType
                        disabled={disableNonEditableFields}
                        featureOptions={
                          features.features[PipelineSettingsFeatures.REPLICATION_TYPE]!
                        }
                      />
                    ) : null}

                    {PipelineSettingsFeatures.SYNC_FREQUENCY in features.features ||
                    PipelineSettingsFeatures.SYNC_FREQUENCY_TYPE in features.features ? (
                      <PipelineSettingsSyncFrequency
                        featureOptions={features}
                        disabled={isFormInteractionBlocked}
                      />
                    ) : null}

                    {PipelineSettingsFeatures.PIPELINE_LATENCY in features.features ? (
                      <PipelineLatency disabled={isFormInteractionBlocked} />
                    ) : null}

                    {mutationState === MutationState.ERRORED && error ? (
                      <Banner
                        className='mt-7'
                        open
                        dataId={PIPELINE_SETTINGS_DATA_IDS_CONTRACT.errorBanner}
                        color='error'
                        icon='error-outline'
                      >
                        Failed to save the form. {getErrorMessageFromAxiosError(error)}
                      </Banner>
                    ) : null}
                  </div>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>

      <div className='setup-guide-docs-common-container'>
        <SetupGuideDocs
          docLink='/pipelines/creating-a-pipeline/'
          isParentLoading={false}
          areTabsRendered
          dataId={PIPELINE_SETTINGS_DATA_IDS_CONTRACT.setupGuideDocs}
        />
      </div>
    </div>
  );
}
